import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import Hero from '../components/Hero';
import BarraBusca from '../components/BarraBusca';
import Carregando from '../components/Carregando';
import { CardPasta, ListaDocumentos, Vazio } from '../components/Documentos';
import { useCatalogo, normalizar, termosDaConsulta } from '../services/catalogo';
import { pluralizar } from '../utils/formatar';
import useTitulo from '../utils/useTitulo';

const LIMITE_FILTRO = 8; // só mostra o filtro em pastas com mais documentos que isso

function Pasta() {
    const { '*': id } = useParams();
    const navigate = useNavigate();
    const { carregando, erro, indice } = useCatalogo();
    const [filtro, setFiltro] = useState('');

    const pasta = indice?.pastasPorId[id];
    useTitulo(pasta?.titulo || 'Pasta não encontrada');

    const termos = useMemo(() => termosDaConsulta(filtro), [filtro]);
    const documentos = useMemo(() => {
        if (!pasta) return [];
        if (!termos.length) return pasta.documentos;
        return pasta.documentos.filter((d) => {
            const texto = normalizar(`${d.titulo} ${d.descricao} ${d.arquivo}`);
            return termos.every((t) => texto.includes(t));
        });
    }, [pasta, termos]);

    if (carregando || erro) {
        return (
            <>
                <Hero titulo="Documentos" />
                <div className="hub-corpo"><Container><Carregando carregando={carregando} erro={erro} /></Container></div>
            </>
        );
    }

    if (!pasta) {
        return (
            <>
                <Hero titulo="Pasta não encontrada" breadcrumb={[{ texto: 'Início', to: '/' }, { texto: 'Pasta não encontrada' }]} />
                <div className="hub-corpo">
                    <Container><Vazio>Esta pasta não existe ou foi renomeada. Volte ao início para ver todas as pastas.</Vazio></Container>
                </div>
            </>
        );
    }

    const breadcrumb = [
        { texto: 'Início', to: '/' },
        ...pasta.caminho.map((p) => ({ texto: p.titulo, to: `/pasta/${p.id}` })),
    ];
    const diretos = pluralizar(pasta.documentos.length, 'documento', 'documentos');

    return (
        <>
            <Hero titulo={pasta.titulo} sub={pasta.descricao} breadcrumb={breadcrumb} />

            <div className="hub-corpo">
                <Container>
                    {pasta.subpastas.length > 0 && (
                        <section aria-labelledby="titulo-subpastas" className="secao">
                            <h2 id="titulo-subpastas" className="secao__titulo">Subpastas</h2>
                            <div className="grade-pastas">
                                {pasta.subpastas.map((s) => (
                                    <CardPasta key={s.id} pasta={indice.pastasPorId[s.id]} />
                                ))}
                            </div>
                        </section>
                    )}

                    {pasta.documentos.length > 0 && (
                        <section aria-labelledby="titulo-docs" className="secao">
                            <h2 id="titulo-docs" className="secao__titulo">
                                Documentos <span className="secao__contagem">({diretos})</span>
                            </h2>

                            {pasta.documentos.length > LIMITE_FILTRO && (
                                <div className="filtro">
                                    <BarraBusca
                                        valor={filtro}
                                        rotulo="Filtrar nesta pasta"
                                        placeholder="Filtrar nesta pasta…"
                                        botao={false}
                                        aoDigitar={setFiltro}
                                        aoEnviar={setFiltro}
                                    />
                                </div>
                            )}

                            {documentos.length > 0 ? (
                                <ListaDocumentos documentos={documentos} termos={termos} />
                            ) : (
                                <Vazio>
                                    Nenhum documento nesta pasta corresponde ao filtro.{' '}
                                    <button type="button" className="link-botao" onClick={() => setFiltro('')}>
                                        Limpar filtro
                                    </button>
                                    {' · '}
                                    <button
                                        type="button"
                                        className="link-botao"
                                        onClick={() => navigate(`/busca?q=${encodeURIComponent(filtro)}`)}
                                    >
                                        Buscar em todas as pastas
                                    </button>
                                </Vazio>
                            )}
                        </section>
                    )}

                    {pasta.documentos.length === 0 && pasta.subpastas.length === 0 && (
                        <Vazio>Esta pasta ainda não tem documentos. Em breve serão disponibilizados.</Vazio>
                    )}
                </Container>
            </div>
        </>
    );
}

export default Pasta;
